import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  InputGroup,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaSearch, FaFilter, FaDownload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const EditUsersLocation = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingAll, setIsUpdatingAll] = useState(false);
  const [locationFilter, setLocationFilter] = useState("all");
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSelectedLocation, setBulkSelectedLocation] = useState("");
  const [selectedShift, setSelectedShift] = useState(null);
  const [bulkSelectedShift, setBulkSelectedShift] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

    const companyKey = sessionStorage.getItem("companyKey");


  // Separate shift states for single user and bulk operations
  const [singleUserAvailableShifts, setSingleUserAvailableShifts] = useState(
    []
  );
  const [bulkAvailableShifts, setBulkAvailableShifts] = useState([]);
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  // Filter users based on search term and location filter
  const filteredUsers = users.filter((user) => {
    // Search term filter
    const matchesSearch =
      !searchTerm ||
      (user.full_name &&
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.mobile_number &&
        user.mobile_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.employee_id &&
        user.employee_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.locationName &&
        user.locationName.toLowerCase().includes(searchTerm.toLowerCase()));

    // Location filter
    const matchesLocation =
      locationFilter === "all" ||
      (locationFilter === "assigned" && user.locationName) ||
      (locationFilter === "unassigned" && !user.locationName) ||
      user.locationName === locationFilter;

    return matchesSearch && matchesLocation;
  });

  useEffect(() => {
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    setTotalPages(totalPages);

    // Reset to page 1 if current page exceeds total pages after items per page change
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredUsers, itemsPerPage, currentPage]);

  // Fetch users data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, locationsRes] = await Promise.all([
          axios.get(
            "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
          ),
          axios.get(
            "https://api.dremerz.net/api/DZ_8922_PayrollProduct_PayrollAddLocations_PayrollLocationDetails"
          ),
        ]);
        const usersData = usersRes.data.filter((d)=> d.companyKey === companyKey);
        const locationsData = locationsRes.data.filter((d) => d.companyKey === companyKey);

        // Filter users with approval_status === "true"
        const approvedUsers = Array.isArray(usersData.reverse())
          ? usersData.filter((user) => user.approval_status === "true")
          : [];

        const assignedUsers = approvedUsers.filter(
          (user) => user.locationName !== "" && user.locationName !== "null"
        );
        setUsers(assignedUsers);

        // setUsers(approvedUsers);

        setLocations(Array.isArray(locationsData) ? locationsData : []);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter locations based on search term
  // useEffect(() => {
  //   if (searchTerm) {
  //     const filtered = locations.filter(location =>
  //       location.locationName && location.locationName.toLowerCase().includes(searchTerm.toLowerCase())
  //     );
  //     setFilteredLocations(filtered);
  //   } else {
  //     setFilteredLocations(locations);
  //   }
  // }, [searchTerm, locations]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = locations.filter((location) => {
        const nameMatch =
          location.locationName &&
          location.locationName
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        const addressMatch =
          location.address &&
          location.address.toLowerCase().includes(searchTerm.toLowerCase());
        return nameMatch || addressMatch;
      });
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations(locations);
    }
  }, [searchTerm, locations]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setSelectedLocation(user.locationName || "");
    setSearchTerm(user.locationName || "");

    // Parse and set available shifts if location has shifts
    if (user.locationName) {
      const location = locations.find(
        (loc) => loc.locationName === user.locationName
      );
      if (location && location.shifts) {
        const shifts = JSON.parse(location.shifts);
        setSingleUserAvailableShifts(shifts);

        // Try to find current shift from user's shift field first
        let currentShift = null;
        if (user.shift) {
          try {
            const userShifts = JSON.parse(user.shift);
            if (Array.isArray(userShifts) && userShifts.length > 0) {
              currentShift = userShifts[0]; // Get the assigned shift
            }
          } catch (e) {
            console.error("Error parsing user shifts:", e);
          }
        }

        // If no current shift found, try to match by startTime
        if (!currentShift && user.startTime) {
          currentShift = shifts.find(
            (shift) =>
              user.startTime && user.startTime.includes(shift.startTime)
          );
        }

        setSelectedShift(currentShift || shifts[0]);
      } else {
        setSingleUserAvailableShifts([]);
        setSelectedShift(null);
      }
    } else {
      setSingleUserAvailableShifts([]);
      setSelectedShift(null);
    }

    setShowModal(true);
  };

  const handleOpenBulkModal = () => {
    if (selectedUsers.length === 0) {
      setError("Please select at least one user");
      return;
    }
    setBulkSelectedLocation("");
    setBulkAvailableShifts([]);
    setBulkSelectedShift(null);
    setShowBulkModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setSelectedLocation("");
    setSearchTerm("");
    setSingleUserAvailableShifts([]);
    setSelectedShift(null);
  };

  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setBulkSelectedLocation("");
    setBulkAvailableShifts([]);
    setBulkSelectedShift(null);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location.locationName);
    setSearchTerm(location.locationName);

    // Parse and set available shifts for single user
    if (location.shifts) {
      const shifts = JSON.parse(location.shifts);
      setSingleUserAvailableShifts(shifts);
      setSelectedShift(shifts[0]); // Default to first shift
    } else {
      setSingleUserAvailableShifts([]);
      setSelectedShift(null);
    }
  };

  const handleBulkLocationSelect = (location) => {
    setBulkSelectedLocation(location.locationName);

    // Parse and set available shifts for bulk selection
    if (location.shifts) {
      const shifts = JSON.parse(location.shifts);
      setBulkAvailableShifts(shifts);
      setBulkSelectedShift(shifts[0]); // Default to first shift
    } else {
      setBulkAvailableShifts([]);
      setBulkSelectedShift(null);
    }
  };

  const handleUserSelect = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(filteredUsers.map((user) => user.id));
    } else {
      setSelectedUsers([]);
    }
  };
  const to24h = (timeStr) => {
    // Accepts 'HH:mm' or 'h[:mm] AM/PM' and returns 'HH:mm'
    if (!timeStr) return "";
    const s = String(timeStr).trim();

    // e.g., "9 AM", "9:30 pm"
    const ampm = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (ampm) {
      let h = parseInt(ampm[1], 10);
      const m = (ampm[2] ?? "00").padStart(2, "0");
      const p = ampm[3].toUpperCase();
      if (p === "PM" && h !== 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
      return `${h.toString().padStart(2, "0")}:${m}`;
    }

    // e.g., "09:05" (already 24h)
    const hm = s.match(/^(\d{1,2}):(\d{2})$/);
    if (hm) {
      let h = Math.max(0, Math.min(23, parseInt(hm[1], 10)));
      const m = hm[2].padStart(2, "0");
      return `${h.toString().padStart(2, "0")}:${m}`;
    }

    // fallback: try to parse hour only
    const hOnly = s.match(/^(\d{1,2})$/);
    if (hOnly) {
      const h = Math.max(0, Math.min(23, parseInt(hOnly[1], 10)));
      return `${h.toString().padStart(2, "0")}:00`;
    }
    return "";
  };
  const formatDisplayTime = (shift) => {
    const s24 = to24h(shift.startTime);
    const e24 = to24h(shift.endTime);
    return `${s24} - ${e24}`;
  };
  
  const handleSubmit = async () => {
    if (!selectedUser || !selectedLocation) return;

    setIsSubmitting(true);

    try {
      const locationToAssign = locations.find(
        (loc) => loc.locationName === selectedLocation
      );

      if (!locationToAssign) {
        throw new Error("Selected location not found");
      }

      // Store old data before update
      const oldUserData = {
        id: selectedUser.id,
        locationName: selectedUser.locationName,
        latitude: selectedUser.latitude,
        longitude: selectedUser.longitude,
        radius: selectedUser.radius,
        address: selectedUser.address,
        shift: selectedUser.shift,
        workingHour: selectedUser.workingHour,
        startTime: selectedUser.startTime,
        endTime: selectedUser.endTime,
      };

      const updatedUser = {
        ...selectedUser,
        locationName: locationToAssign.locationName,
        latitude: locationToAssign.latitude,
        longitude: locationToAssign.longitude,
        radius: locationToAssign.radius,
        address: locationToAssign.address,
        shift: selectedShift
          ? JSON.stringify([selectedShift])
          : locationToAssign.shifts,
        workingHour: selectedShift?.workingHours?.toString() || "",
        startTime: selectedShift ? `${selectedShift.startTime}` : "",
        endTime: selectedShift ? `${selectedShift.endTime}` : "",
        // breakTime: selectedShift? `${selectedShift.breakTime}` : "",
      };

      const response = await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`,
        updatedUser
      );
      doAuditLog("Update", "Edit Employee Location", selectedUser, updatedUser);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Updated",
        message: `User updated successfully`,
        duration: 2500, // auto-close in 2.5s
      });

      const updatedUserFromResponse = response.data.data || response.data;

      setUsers(
        users.map((user) =>
          user.id === selectedUser.id ? updatedUserFromResponse : user
        )
      );

      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedUsers.length === 0 || !bulkSelectedLocation) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const locationToAssign = locations.find(
        (loc) => loc.locationName === bulkSelectedLocation
      );

      if (!locationToAssign) {
        throw new Error("Selected location not found");
      }

      const updatePromises = selectedUsers.map(async (userId) => {
        const user = users.find((u) => u.id === userId);
        if (!user) return null;

        // Store old data before update
        const oldUserData = {
          id: user.id,
          locationName: user.locationName,
          latitude: user.latitude,
          longitude: user.longitude,
          radius: user.radius,
          address: user.address,
          shift: user.shift,
          workingHour: user.workingHour,
          startTime: user.startTime,
          endTime: user.endTime,
        };

        const updatedUser = {
          ...user,
          locationName: locationToAssign.locationName,
          latitude: locationToAssign.latitude,
          longitude: locationToAssign.longitude,
          radius: locationToAssign.radius,
          address: locationToAssign.address,
          shift: bulkSelectedShift
            ? JSON.stringify([bulkSelectedShift])
            : locationToAssign.shifts,
          workingHour: bulkSelectedShift?.workingHours?.toString() || "",
          startTime: bulkSelectedShift ? `${bulkSelectedShift.startTime}` : "",
          endTime: bulkSelectedShift ? `${bulkSelectedShift.endTime}` : "",
          // breakTime: bulkSelectedShift? `${bulkSelectedShift.breakTime}` : "",
        };

        const response = await axios.put(
          `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${userId}`,
          updatedUser
        );
        doAuditLog("Update", "Edit Employee Location", user, updatedUser);
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "Updated",
          message: `User ${user.full_name} updated successfully`,
          duration: 2500, // auto-close in 2.5s
        });
        const updatedUserFromResponse = response.data.data || response.data;

        return updatedUserFromResponse;
      });

      const results = await Promise.all(updatePromises);

      setUsers((prevUsers) =>
        prevUsers.map((user) => {
          const updatedUser = results.find(
            (result) => result && result.id === user.id
          );
          return updatedUser || user;
        })
      );

      setSelectedUsers([]);
      handleCloseBulkModal();
    } catch (err) {
      setError(`Failed to update locations: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    const tableColumn = [
      "#",
      "Full Name",
      "Mobile Number",
      "Employee ID",
      "Location Status",
    ];
    const tableRows = filteredUsers.map((user, index) => [
      index + 1,
      user.full_name || "N/A",
      user.mobile_number || "N/A",
      user.employee_id || "N/A",
      user.locationName ? `Assigned: ${user.locationName}` : "Not Assigned",
    ]);
    if (tableRows.length === 0) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No data to download for selected criteria",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Employee Location", 14, 16);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Manage_Employee_Location.pdf");
  };

  const handleDownloadExcel = () => {
    if (filteredUsers.length === 0) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No data to download for selected criteria",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const worksheetData = filteredUsers.map((user, index) => ({
      "S.I": index + 1,
      "Full Name": user.full_name || "N/A",
      "Mobile Number": user.mobile_number || "N/A",
      "Employee ID": user.employee_id || "N/A",
      "Location Name": user.locationName || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Location");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const fileName = `Edit_Employee_Location_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    saveAs(blob, fileName);
  };

  const isMobile = window.innerWidth < 768;

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        variant="danger"
        className="mt-3"
        onClose={() => setError(null)}
        dismissible
      >
        Error: {error}
      </Alert>
    );
  }

  // Get unique location names for filter dropdown
  const uniqueLocations = [
    ...new Set(users.map((user) => user.locationName).filter(Boolean)),
  ];

  return (
    <div className="container mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-6">
          <h5 className="mb-2 mb-md-0 d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
            Edit Employee Location
          </h5>
        </div>
        <div className="col-12 col-md-6 text-end">
          <div className="d-flex justify-content-center justify-content-md-end gap-2">
            <Button
              variant="outline-success"
              size="sm"
              className="d-flex align-items-center gap-1"
              style={{ padding: "2px 8px" }}
              title="Download Excel"
              onClick={handleDownloadExcel}
            >
              Export Excel
            </Button>

            {/* PDF Export Button */}
            <Button
              variant="outline-danger"
              size="sm"
              className="d-flex align-items-center gap-1"
              style={{ padding: "2px 8px" }}
              title="Download PDF"
              onClick={handleDownloadPDF}
            >
              Export PDF
            </Button>

            {canEdit && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowBulkModal(true)}
                disabled={selectedUsers.length === 0}
              >
                Bulk Edit Location
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2 mx-1">
        {/* Search input */}
        <div className="col-12 col-md-5 mb-2 mb-md-0">
          <Form.Group className="mb-0">
            <Form.Label className="small text-muted mb-1">Search</Form.Label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-light border-end-0">
                <FaSearch className="text-muted" />
              </span>
              <Form.Control
                type="text"
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0"
              />
            </div>
          </Form.Group>
        </div>

        {/* Location filter */}
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <Form.Group className="mb-0">
            <Form.Label className="small text-muted mb-1">Location</Form.Label>
            <Form.Select
              size="sm"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="all">All Locations</option>
              {uniqueLocations.map((location, index) => (
                <option key={index} value={location}>
                  {location}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {/* Clear button */}
        <div className="col-12 col-md-3 d-flex justify-content-md-end mt-2 mt-md-4">
          <Button
            variant="outline-danger"
            size="sm"
            className="w-100 w-md-auto"
            onClick={() => {
              setSearchTerm("");
              setLocationFilter("all");
            }}
          >
            Clear
          </Button>
        </div>
      </div>
      {/* Pagination Section */}
      {filteredUsers.length > 0 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto mb-2 mb-md-0">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="items-per-page-dropdown"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show full pagination */
                Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                )
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}
      {users.length === 0 ? (
        <Alert variant="info">No users found</Alert>
      ) : isMobile ? (
        <div>
          {currentRecords.map((user, index) => (
            <div key={user.id} className="mb-3">
              <div
                className="card shadow-sm"
                style={{ textAlign: "left", borderRadius: "12px" }}
              >
                <div className="card-body table-responsive">
                  <table className="table table-striped table-bordered mb-2">
                    <tbody>
                      <tr>
                        <td className="fw-bold">SI</td>
                        <td>{currentRecords.length - index}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Full Name</td>
                        <td>{user.full_name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Mobile Number</td>
                        <td>{user.mobile_number || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Employee ID</td>
                        <td>{user.employee_id || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Location Status</td>
                        <td>
                          {user.locationName ? (
                            <span>Assigned: {user.locationName}</span>
                          ) : (
                            <span className="text-dark">Not Assigned</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {canEdit && (
                    <div className="d-flex gap-2 justify-content-between align-itmes-center flex-wrap">
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) =>
                          handleUserSelect(user.id, e.target.checked)
                        }
                        label="Select"
                      />

                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleOpenModal(user)}
                      >
                        {user.locationName
                          ? "Edit Location"
                          : "Assign Location"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th id="text-white-important">
                <Form.Check
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={
                    selectedUsers.length === filteredUsers.length &&
                    filteredUsers.length > 0
                  }
                />
              </th>
              <th id="text-white-important">S.I</th>
              <th id="text-white-important">Full Name</th>
              <th id="text-white-important">Mobile Number</th>
              <th id="text-white-important">Employee ID</th>
              <th id="text-white-important">Location Status</th>
              {canEdit && <th id="text-white-important">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {currentRecords.map((user, index) => (
              <tr key={user.id}>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) =>
                      handleUserSelect(user.id, e.target.checked)
                    }
                  />
                </td>
                <td>{filteredUsers.length - index}</td>
                <td>{user.full_name || "N/A"}</td>
                <td>{user.mobile_number || "N/A"}</td>
                <td>{user.employee_id || "N/A"}</td>
                <td>
                  <span className="">{user.locationName}</span>
                </td>
                {canEdit && (
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleOpenModal(user)}
                    >
                      {user.locationName ? "Edit Location" : "Assign Location"}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Showing entries info */}
      <div className="col-12 text-end mb-2">
        <span className="text-muted small">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredUsers.length)} of{" "}
          {filteredUsers.length} entries
        </span>
      </div>

      {/* Single User Location Assignment Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedUser?.locationName ? "Edit Location" : "Assign Location"}{" "}
            for {selectedUser?.full_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search Location</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Type to search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Form.Group>

          <div className="location-dropdown">
            {filteredLocations.length > 0 ? (
              <ul
                className="list-group"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                {filteredLocations.map((location) => (
                  <li
                    key={location.id}
                    className={`list-group-item list-group-item-action ${
                      selectedLocation === location.id ? "active" : ""
                    }`}
                    onClick={() => handleLocationSelect(location)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="fw-bold">
                      {location.locationName || "Unnamed Location"}
                    </div>
                    {location.address && (
                      <div className="text-muted small">{location.address}</div>
                    )}
                    <div className="text-muted small mt-1">
                      Coordinates: {location.latitude}, {location.longitude} |
                      Radius: {location.radius}m
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-muted p-2">No locations found</div>
            )}
          </div>

          {selectedLocation && singleUserAvailableShifts.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Select Shift</Form.Label>
              <Form.Select
                value={selectedShift ? JSON.stringify(selectedShift) : ""}
                onChange={(e) =>
                  setSelectedShift(
                    e.target.value ? JSON.parse(e.target.value) : null
                  )
                }
              >
                <option value="">Select a shift</option>
                {singleUserAvailableShifts.map((shift, index) => (
                  <option key={index} value={JSON.stringify(shift)}>
                    {shift.shiftName} ({formatDisplayTime(shift)})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {selectedLocation && (
            <div className="mt-3 p-3 bg-light rounded">
              <h6>Selected Location:</h6>
              <p className="mb-1">
                <strong>Name:</strong> {selectedLocation}
              </p>
              <p className="mb-1">
                <strong>Address:</strong>{" "}
                {
                  locations.find((loc) => loc.locationName === selectedLocation)
                    ?.address
                }
              </p>
              {selectedShift && (
                <>
                  <p className="mb-1">
                    <strong>Shift:</strong> {selectedShift.shiftName}
                  </p>
                  <p className="mb-1">
                    <strong>Working Hours:</strong> {selectedShift.workingHours}{" "}
                    hours
                  </p>
                  <p className="mb-1">
                    <strong>Time:</strong> {selectedShift.startTime} -{" "}
                    {selectedShift.endTime}
                  </p>
                  {/* <p className="mb-0">
                    <strong>Break Time:</strong> {selectedShift.breakTime} mins
                  </p> */}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!selectedLocation || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Location Assignment Modal */}
      <Modal show={showBulkModal} onHide={handleCloseBulkModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            Assign Location to {selectedUsers.length} Selected Users
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Location</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Type to search locations..."
                value={bulkSelectedLocation}
                onChange={(e) => setBulkSelectedLocation(e.target.value)}
              />
            </InputGroup>
          </Form.Group>

          <div className="location-dropdown">
            {locations.length > 0 ? (
              <ul
                className="list-group"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                {locations
                  .filter(
                    (location) =>
                      !bulkSelectedLocation ||
                      (location.locationName &&
                        location.locationName
                          .toLowerCase()
                          .includes(bulkSelectedLocation.toLowerCase()))
                  )
                  .map((location) => (
                    <li
                      key={location.id}
                      className={`list-group-item list-group-item-action ${
                        bulkSelectedLocation === location.locationName
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleBulkLocationSelect(location)}
                      style={{ cursor: "pointer" }}
                    >
                      {location.locationName || "Unnamed Location"}
                      <div className="text-muted small">
                        Lat: {location.latitude}, Long: {location.longitude},
                        Radius: {location.radius}m
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <div className="text-muted p-2">No locations found</div>
            )}
          </div>

          {bulkSelectedLocation && bulkAvailableShifts.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Select Shift</Form.Label>
              <Form.Select
                value={
                  bulkSelectedShift ? JSON.stringify(bulkSelectedShift) : ""
                }
                onChange={(e) =>
                  setBulkSelectedShift(
                    e.target.value ? JSON.parse(e.target.value) : null
                  )
                }
              >
                <option value="">Select a shift</option>
                {bulkAvailableShifts.map((shift, index) => (
                  <option key={index} value={JSON.stringify(shift)}>
                    {shift.shiftName} ({formatDisplayTime(shift)})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseBulkModal}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkSubmit}
            disabled={!bulkSelectedLocation || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Assigning...</span>
              </>
            ) : (
              `Assign to ${selectedUsers.length} Users`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditUsersLocation;
